import { Injectable } from '@angular/core';
import {
  ActionPlanModel,
  ActionPlanItem,
  ActionPlanGroupedItemsModel,
  Inspection,
  ActionPlanPlugin,
  Icon
} from '../models/index';
import { Observable, BehaviorSubject } from 'rxjs';
import * as _ from 'lodash';
import { PluginComponent } from '../../plugins/plugin.component';
import { TranslationService } from './translation.service';

@Injectable({
  providedIn: 'root'
})
export class ActionPlanDataService {
  public actionPlansById: any = {};
  private loaded = false;

  constructor(private translationService: TranslationService) {}

  public isLoaded(): boolean {
    return this.loaded;
  }

  public getActionPlan(id: string): ActionPlanModel {
    const actionPlan = this.actionPlansById[id];
    return actionPlan;
  }

  public getActionPlans(): Observable<ActionPlanModel[]> {
    return new BehaviorSubject(_.values(this.actionPlansById)).asObservable();
  }

  public getActionPlanItems(actionPlanId: string): Observable<ActionPlanItem[]> {
    const actionPlan: ActionPlanModel = this.actionPlansById[actionPlanId];
    return new BehaviorSubject(actionPlan ? actionPlan.actionPlanItems : []).asObservable();
  }

  public getActionPlanItem(actionPlanId: string, actionPlanItemId: string): Observable<ActionPlanItem> {
    const actionPlan: ActionPlanModel = this.actionPlansById[actionPlanId];
    if (actionPlan == null) {
      return new BehaviorSubject(null).asObservable();
    }
    const actionPlanItem = actionPlan.actionPlansItemsById[actionPlanItemId];
    return new BehaviorSubject(actionPlanItem).asObservable();
  }

  public processActionPlans(actionPlans: ActionPlanModel[]) {
    actionPlans.forEach((a) => {
      this.processActionPlan(a);
    });
    for (let i = 0; i < actionPlans.length; i++) {
      const a = actionPlans[i];
      actionPlans[i] = this.processActionPlan(a);
    }
    this.loaded = true;
    return _.values(this.actionPlansById);
  }

  public processActionPlan(actionPlan: ActionPlanModel, inspection?: Inspection) {
    let current: ActionPlanModel = this.actionPlansById[actionPlan.id];
    if (inspection) {
      actionPlan.inspection = inspection;
    }
    if (actionPlan.inspection && !('search' in actionPlan.inspection)) {
      const insp = actionPlan.inspection;
      actionPlan.inspection = _.merge(new Inspection(insp['id'], insp['name'], insp['description']), insp);
    }
    if (current == null) {
      this.actionPlansById[actionPlan.id] = _.merge(
        new ActionPlanModel(actionPlan.id, actionPlan.name, actionPlan.description),
        actionPlan
      );
    } else {
      actionPlan.name = 'actionPlan.name'; // TO DELETE
      this.actionPlansById[actionPlan.id] = Object.assign(current, actionPlan);
    }

    current = this.actionPlansById[actionPlan.id];
    this.processActionPlanItems(current, current.actionPlanItems);
    this.processPlugins(current, current.allPlugins);

    if (current.translations) {
      this.translationService.applyTranslations(current, current.translations, current.inputLanguage || 'en');
    }
    return current;
  }

  public processActionPlanItems(actionPlan: ActionPlanModel, actionPlanItems: ActionPlanItem[]) {
    for (let i = 0; i < actionPlanItems.length; i++) {
      const a = actionPlanItems[i];
      actionPlanItems[i] = this.processActionPlanItem(actionPlan, a);
    }
    this.setResponsibleList(actionPlan.id);
  }

  public processActionPlanItem(actionPlan: ActionPlanModel, actionPlanItem: ActionPlanItem) {
    if (!('search' in actionPlanItem)) {
      actionPlanItem = _.merge(new ActionPlanItem(), actionPlanItem);
    }

    if (!actionPlan.actionPlansItemsById) {
      actionPlan.actionPlansItemsById = {};
    }

    if (actionPlan.inspection) {
      actionPlanItem.point = actionPlan.inspection.pointsById[actionPlanItem.pointId];
      const observationPlugin = actionPlan.inspection.pluginsById[actionPlanItem.pluginId];
      if (observationPlugin && observationPlugin.config.ObservationsAndRecommandations) {
        const observation = observationPlugin.config.ObservationsAndRecommandations.find(
          (o) => o.Id === actionPlanItem.parentId
        );
        actionPlanItem.observationPlugin = observation ? observation : null;
      }
      if (actionPlanItem.point) {
        actionPlanItem.section = actionPlan.inspection.pointsById[actionPlanItem.pointId].section;
        actionPlan.actionPlansItemsById[actionPlanItem.id] = actionPlanItem;
      }
    }
    return actionPlanItem;
  }

  public processPlugins(actionPlan: ActionPlanModel, plugins: ActionPlanPlugin[]) {
    actionPlan.plugins = [];
    for (let i = 0; i < plugins.length; i++) {
      const plugin = plugins[i];
      plugins[i] = this.processPlugin(
        actionPlan,
        _.merge(new ActionPlanPlugin(plugin.id, plugin.order, plugin.pluginType), plugin)
      );
    }
  }

  public processPlugin(actionPlan: ActionPlanModel, plugin: ActionPlanPlugin): ActionPlanPlugin {
    if (!('search' in plugin)) {
      plugin = _.merge(new ActionPlanPlugin(plugin['id'], plugin['order'], plugin['pluginType']), plugin);
    }
    plugin.actionPlan = actionPlan;
    if (plugin.actionPlanItemId) {
      plugin.actionPlanItem = actionPlan.actionPlansItemsById[plugin.actionPlanItemId];
      if (plugin.actionPlanItem) {
        const i = _.findIndex(plugin.actionPlanItem.plugins, ['id', plugin.id]);
        if (i !== -1) {
          plugin.order = plugin.actionPlanItem.plugins[i].order;
          plugin.actionPlanItem.plugins[i] = plugin;
        } else {
          plugin.actionPlanItem.plugins.push(plugin);
        }
      }
    }
    if (!actionPlan.pluginsById) {
      actionPlan.pluginsById = {};
    }

    actionPlan.pluginsById[plugin.id] = plugin;
    if (PluginComponent.initializers[plugin.pluginType]) {
      PluginComponent.initializers[plugin.pluginType](plugin);
    }
    return plugin;
  }

  private setResponsibleList(actionPlanId: string) {
    const responsibleList: ActionPlanGroupedItemsModel[] = [];
    const actionPlan: ActionPlanModel = this.actionPlansById[actionPlanId];
    const groupedItems = _.groupBy(actionPlan.actionPlanItems, 'category');
    _.each(groupedItems, (value, key) => {
      const el = new ActionPlanGroupedItemsModel(key, value);
      responsibleList.push(el);
    });
    // actionPlan.actionPlanItems.forEach(i => {
    //    if (!responsibleList.some(r => r.side === i.responsible)) {
    //        responsibleList.push(new ActionPlanGroupedItemsModel(i.responsible));
    //    }
    // });
    actionPlan.responsibleList = responsibleList;
  }

  public getPlugin(actionPlanId: string, pluginId: string): Observable<ActionPlanPlugin> {
    const actionPlan = this.actionPlansById[actionPlanId];
    if (actionPlan == null) {
      return new BehaviorSubject(null).asObservable();
    }
    const plugin = actionPlan.pluginsById[pluginId];
    return new BehaviorSubject(plugin).asObservable();
  }

  public updateActionPlanItemStatus(actionPlanId: string, actionPlanItemId: string, data: any): void {
    const actionPlan: ActionPlanModel = this.actionPlansById[actionPlanId];
    if (actionPlan != null) {
      actionPlan.prog = data.actionPlan.prog;
      const actionPlanItem = actionPlan.actionPlansItemsById[actionPlanItemId];
      actionPlanItem.status = data.actionPlanItem.status;
    }
  }

  public updatePlugin(id: string, plugin: ActionPlanPlugin): any {
    const current: Inspection = this.actionPlansById[id];
    if (current) {
      const p: ActionPlanPlugin = current.pluginsById[plugin.id];
      p.config = plugin.config;
      p.order = plugin.order;
    }
  }

  public removeActionPlan(actionPlanId: string) {
    delete this.actionPlansById[actionPlanId];
  }

  public updateActionPlanItems(id: string, actionPlanItems: ActionPlanItem[]): ActionPlanItem[] {
    const current: ActionPlanModel = this.actionPlansById[id];
    if (current) {
      for (let i = 0; i < actionPlanItems.length; i++) {
        const actionPlanItem = actionPlanItems[i];
        const mergedActionPlanItem = _.merge(new ActionPlanItem(), actionPlanItem);
        current.actionPlanItems = current.actionPlanItems.filter((a) => a.id !== actionPlanItem.id);
        current.actionPlanItems.push(actionPlanItem);
        actionPlanItems[i] = this.processActionPlanItem(current, mergedActionPlanItem);
        this.setResponsibleList(current.id);
      }
    }
    return actionPlanItems;
  }

  public addPlugin(id: string, plugins: ActionPlanPlugin[]): any {
    const current: ActionPlanModel = this.actionPlansById[id];
    if (current) {
      for (let i = 0; i < plugins.length; i++) {
        const plugin = plugins[i];
        this.processPlugin(current, _.merge(new ActionPlanPlugin(plugin.id, plugin.order, plugin.pluginType), plugin));
      }
    }
  }

  removeActionPlanItemsByObservationIds(id: string, observationsIds: string[]) {
    const current: ActionPlanModel = this.actionPlansById[id];
    if (current) {
      observationsIds.forEach((observationId) => {
        const actionPlanItem = current.actionPlanItems.find((a) => a.parentId === observationId);
        if (actionPlanItem) {
          _.remove(current.actionPlanItems, current.actionPlansItemsById[actionPlanItem.id]);
          delete current.actionPlansItemsById[actionPlanItem.id];
        }
        this.setResponsibleList(current.id);
      });
    }
  }

  removeActionPlanItemsPointId(id: string, pointId: string) {
    const current: ActionPlanModel = this.actionPlansById[id];
    if (current) {
      const actionPlanItems = current.actionPlanItems.filter((a) => a.pointId === pointId);
      if (actionPlanItems && actionPlanItems.length > 0) {
        actionPlanItems.forEach((item) => {
          _.remove(current.actionPlanItems, current.actionPlansItemsById[item.id]);
          delete current.actionPlansItemsById[item.id];
        });
        this.setResponsibleList(current.id);
      }
    }
  }

  public processIcons(toAdd: boolean, icon: Icon, actionPlanId: string, itemId: string) {
    const actionPlan: ActionPlanModel = this.actionPlansById[actionPlanId];
    const item: ActionPlanItem = actionPlan.actionPlansItemsById[itemId];
    const iconName = icon.name;
    const iconClasses = icon.classes;
    if (!item) {
      return;
    }

    if (
      (toAdd && !item.icons.some((i) => i.name === iconName && i.classes === iconClasses)) ||
      (!toAdd && item.icons.some((i) => i.name === iconName && i.classes === iconClasses))
    ) {
      if (toAdd) {
        item.icons.push(icon);
      }

      if (!toAdd) {
        item.icons = item.icons.filter((i) => i.name !== iconName || i.classes !== iconClasses);
      }

      if (actionPlan.responsibleList && actionPlan.responsibleList[item.responsible]) {
        const responsible = actionPlan.responsibleList[item.responsible];
        if (responsible.icons.some((i) => i.name === iconName && i.classes === iconClasses)) {
          const icon = responsible.icons.find((i) => i.name === iconName && i.classes === iconClasses);
          toAdd ? icon.count++ : icon.count--;
        } else {
          if (toAdd) {
            icon.count = 1;
            responsible.icons.push(icon);
          } else {
            responsible.icons = responsible.icons.filter((i) => i.name !== iconName || i.classes !== iconClasses);
          }
        }
      }
    }
  }
}
